import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api, keys, useMe } from '../../api.ts';
import { userCreateSchema, userUpdateSchema } from '../../../shared/schemas.ts';
import type { UserDetail } from '../../../shared/types.ts';
import {
  applyServerErrors,
  ConfirmButton,
  ErrorBox,
  Field,
  Help,
  PageHeader,
  Spinner,
  useDocumentTitle,
  useToast,
} from '../../components/ui.tsx';
import { useUserContext } from './UserLayout.tsx';

type Values = z.input<typeof userCreateSchema>;

/** Sama lomake toimii uuden käyttäjän luonnissa (ylläpitäjä) ja omien tietojen muokkauksessa */
export function UserEditPage({ create = false }: { create?: boolean }) {
  if (create) return <UserForm user={null} />;
  return <EditExisting />;
}

function EditExisting() {
  const { user } = useUserContext();
  return <UserForm user={user} />;
}

function UserForm({ user }: { user: UserDetail | null }) {
  useDocumentTitle(user ? `${user.id} – muokkaa` : 'Uusi käyttäjä');
  const { data: me } = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const schema = user ? userUpdateSchema : userCreateSchema;
  const { register, handleSubmit, formState, setError, reset } = useForm<Values>({
    resolver: zodResolver(schema as typeof userCreateSchema),
    defaultValues: {
      id: user?.id ?? '',
      name: user?.name ?? '',
      email: user?.email ?? '',
      address1: user?.address1 ?? '',
      address2: user?.address2 ?? '',
      postcode: user?.postcode ?? '',
      city: user?.city ?? '',
      licenceDate: user?.licenceDate ?? '',
      genre: user?.genre ?? 'OM',
      userClass: user?.userClass ?? 'Yleisluokka',
      // radiopainikkeiden arvot ovat merkkijonoja; zod muuntaa numeroksi
      roleId: String(user?.roleId ?? 2),
      active: user?.active ?? true,
      password: '',
      passwordConfirm: '',
    },
  });
  const e = formState.errors;

  const save = useMutation({
    mutationFn: (d: Values) =>
      user
        ? api.put<{ activated?: boolean }>(`/users/${encodeURIComponent(user.id)}`, d)
        : api.post<{ activated?: boolean }>('/users', d),
    onSuccess: (res, d) => {
      void qc.invalidateQueries({ queryKey: ['user'] });
      void qc.invalidateQueries({ queryKey: keys.users });
      if (user && me?.id === user.id) void qc.invalidateQueries({ queryKey: keys.me });
      if (!user) {
        toast.ok(`Käyttäjä ${d.id} luotu`);
        navigate(`/users/${encodeURIComponent(String(d.id).toUpperCase())}/edit`);
      } else {
        toast.ok((res as { activated?: boolean }).activated ? 'Tallennettu. Käyttäjä aktivoitiin.' : 'Tiedot tallennettu');
        reset({ ...d, password: '', passwordConfirm: '' });
      }
    },
    onError: (err) => {
      if (!applyServerErrors(err, setError)) toast.error(err);
    },
  });

  const sendActivation = useMutation({
    mutationFn: () => api.post<{ email: string }>(`/users/${encodeURIComponent(user!.id)}/send-activation`),
    onSuccess: (r) => toast.ok(`Aktivointiviesti lähetetty osoitteeseen ${r.email}`),
    onError: toast.error,
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/users/${encodeURIComponent(user!.id)}`),
    onSuccess: () => {
      toast.ok(`Käyttäjä ${user!.id} poistettu`);
      void qc.invalidateQueries({ queryKey: keys.users });
      navigate('/users');
    },
    onError: toast.error,
  });

  return (
    <div className={user ? 'page-narrow' : 'page page-narrow'}>
      {!user && <PageHeader title="Uusi käyttäjä" />}
      <form className="card form" onSubmit={handleSubmit((d) => save.mutate(d))} noValidate>
        <div className="card-title">
          <h2>{user ? 'Muokkaa tietoja' : 'Käyttäjän tiedot'}</h2>
          <Help name="KayttajanMuokkaus" />
        </div>
        <div className="form-grid">
          {!user && (
            <Field label="Kutsu" error={e.id} htmlFor="id">
              <input id="id" className="input mono" {...register('id')} />
            </Field>
          )}
          <Field label="Nimi" error={e.name} htmlFor="name">
            <input id="name" className="input" {...register('name')} />
          </Field>
          <Field label="Sähköposti" error={e.email} htmlFor="email">
            <input id="email" type="email" className="input" {...register('email')} />
          </Field>
          <Field label="Lisenssi saatu" error={e.licenceDate} htmlFor="licenceDate">
            <input id="licenceDate" type="date" className="input" {...register('licenceDate')} />
          </Field>
          <Field label="Osoite" error={e.address1} htmlFor="address1">
            <input id="address1" className="input" {...register('address1')} />
          </Field>
          <Field label="Osoite 2" error={e.address2} htmlFor="address2">
            <input id="address2" className="input" {...register('address2')} />
          </Field>
          <Field label="Postinumero" error={e.postcode} htmlFor="postcode">
            <input id="postcode" className="input" {...register('postcode')} />
          </Field>
          <Field label="Kaupunki" error={e.city} htmlFor="city">
            <input id="city" className="input" {...register('city')} />
          </Field>
        </div>
        <div className="grid grid-3">
          <fieldset>
            <legend>Luokka</legend>
            <div className="radio-group">
              <label className="check">
                <input type="radio" value="Yleisluokka" {...register('userClass')} /> Yleisluokka
              </label>
              <label className="check">
                <input type="radio" value="Perusluokka" {...register('userClass')} /> Perusluokka
              </label>
            </div>
          </fieldset>
          <fieldset>
            <legend>Sukupuoli</legend>
            <div className="radio-group">
              <label className="check">
                <input type="radio" value="OM" {...register('genre')} /> OM
              </label>
              <label className="check">
                <input type="radio" value="YL" {...register('genre')} /> YL
              </label>
            </div>
          </fieldset>
          {me?.isAdmin && (
            <fieldset>
              <legend>Rooli ja tila</legend>
              <div className="radio-group">
                <label className="check">
                  <input type="radio" value={2} {...register('roleId')} /> Käyttäjä
                </label>
                <label className="check">
                  <input type="radio" value={1} {...register('roleId')} /> Ylläpitäjä
                </label>
              </div>
              <label className="check" style={{ marginTop: 10 }}>
                <input type="checkbox" {...register('active')} /> Aktiivinen
              </label>
            </fieldset>
          )}
        </div>
        <fieldset>
          <legend>{user ? 'Vaihda salasana' : 'Salasana'}</legend>
          <div className="form-grid">
            <Field label="Uusi salasana" error={e.password} hint={user ? 'Jätä tyhjäksi, jos et vaihda' : undefined} htmlFor="password">
              <input id="password" type="password" className="input" autoComplete="new-password" {...register('password')} />
            </Field>
            <Field label="Toista salasana" error={e.passwordConfirm} htmlFor="passwordConfirm">
              <input id="passwordConfirm" type="password" className="input" autoComplete="new-password" {...register('passwordConfirm')} />
            </Field>
          </div>
        </fieldset>
        <ErrorBox error={save.error && !(save.error as { issues?: unknown[] }).issues?.length ? save.error : null} />
        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={save.isPending}>
            {save.isPending ? <Spinner /> : 'Tallenna'}
          </button>
          {user && me?.isAdmin && (
            <>
              <button type="button" className="btn" disabled={!user.active || sendActivation.isPending} onClick={() => sendActivation.mutate()}>
                Lähetä aktivointiviesti
              </button>
              <span className="spacer" />
              {me.id !== user.id && (
                <ConfirmButton
                  title="Poista käyttäjä"
                  message={`Haluatko varmasti poistaa käyttäjän ${user.id} ja kaikki käyttäjän QSO:t?`}
                  confirmText="Poista"
                  onConfirm={() => remove.mutateAsync()}
                >
                  Poista käyttäjä
                </ConfirmButton>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}
